import { atmosphereGLSL } from "./atmosphere.ts";
import { OCEAN_LEVELS, OCEAN_SPACING } from "./ocean-mesh.ts";
import { SHORE } from "./shore-bake.ts";

const SURFACE_NOISE = `
float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p), u = f * f * (3.0 - 2.0 * f);
  float a = hash12(i), b = hash12(i + vec2(1.0, 0.0));
  float c = hash12(i + vec2(0.0, 1.0)), d = hash12(i + vec2(1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
vec2 vnoiseGrad(vec2 p) {
  vec2 i = floor(p), f = fract(p), u = f * f * (3.0 - 2.0 * f);
  vec2 du = 6.0 * f * (1.0 - f);
  float a = hash12(i), b = hash12(i + vec2(1.0, 0.0));
  float c = hash12(i + vec2(0.0, 1.0)), d = hash12(i + vec2(1.0));
  return du * vec2(b - a + (a - b - c + d) * u.y, c - a + (a - b - c + d) * u.x);
}`;

export const SKY_VS = `
precision highp float;
attribute vec3 position;
uniform mat4 world;
uniform mat4 viewProjection;
varying vec3 vDir;
void main(void) {
  vDir = position;
  gl_Position = viewProjection * world * vec4(position, 1.0);
}`;

export function skyFragmentShader(cloudSteps: 48 | 96): string {
  return `
precision highp float;
uniform vec3 sunDir;
uniform vec3 zenith;
uniform vec3 horizon;
uniform float time;
uniform float storm;
varying vec3 vDir;
${atmosphereGLSL(cloudSteps)}
void main(void) { gl_FragColor = vec4(atmosphereRadiance(normalize(vDir)), 1.0); }
`;
}

/** Displacement, normals and compression share the exact components of `waveSurface` (scene/waves.ts). */
const WATER_WAVES = `
#define WAVE_COMPONENTS 32
// Per component: waveK = (kx, kz, omega, phase); waveA = (amplitude m, lateral m, mesh 0/1, 1/|k|).
uniform vec4 waveK[WAVE_COMPONENTS];
uniform vec4 waveA[WAVE_COMPONENTS];
uniform float waveCount;
// 1, or the reduced-motion fraction.
uniform float waveScale;
// Offset (x, height, z) of rest point p from components that displace the grid, before waveScale.
// Components shorter than four cells of the local vertex spacing would alias: they fade out from
// 3.9 to 3 cells. Shorter mesh components (sines) count as 3.9 of the 2 m cells, so the 2 m grid
// keeps every mesh component at full weight. Components come longest first (waveSurface), so the
// first that skips the grid or is gone ends the loop.
vec3 waveOffset(vec2 p, float spacing) {
  vec3 o = vec3(0.0);
  for (int i = 0; i < WAVE_COMPONENTS; i++) {
    if (float(i) >= waveCount) break;
    vec4 k = waveK[i], a = waveA[i];
    if (a.z < 0.5) break;
    float fade = smoothstep(3.0, 3.9, max(6.2831853 * a.w, 3.9 * OCEAN_SPACING) / spacing);
    if (fade <= 0.0) break;
    float th = dot(k.xy, p) + time * k.z + k.w;
    float lateral = a.y * a.w * cos(th);
    o += vec3(k.x * lateral, a.x * sin(th), k.y * lateral) * fade;
  }
  return o;
}
// Height gradient and compression (1 - Jacobian) at rest point p from the first waveNormals
// components (graphics preset); they fade out as their wavelength nears the pixel footprint, and
// (longest first) the first gone ends the loop.
uniform float waveNormals;
void waveFrame(vec2 p, float dist, out vec2 slope, out float compression) {
  float hx = 0.0, hz = 0.0, jxx = 0.0, jzz = 0.0, jxz = 0.0;
  for (int i = 0; i < WAVE_COMPONENTS; i++) {
    if (float(i) >= min(waveCount, waveNormals)) break;
    vec4 k = waveK[i], a = waveA[i];
    float fade = 1.0 - smoothstep(0.25, 0.5, dist * 0.005 / (6.2831853 * a.w));
    if (fade <= 0.0) break;
    float th = dot(k.xy, p) + time * k.z + k.w;
    float h = a.x * cos(th) * fade;
    float q = a.y * a.w * sin(th) * fade;
    hx += k.x * h;
    hz += k.y * h;
    jxx += k.x * k.x * q;
    jzz += k.y * k.y * q;
    jxz += k.x * k.y * q;
  }
  vec3 dx = vec3(1.0 - jxx * waveScale, hx * waveScale, -jxz * waveScale);
  vec3 dz = vec3(-jxz * waveScale, hz * waveScale, 1.0 - jzz * waveScale);
  vec3 n = cross(dz, dx);
  slope = -n.xz / n.y;
  compression = 1.0 - (dx.x * dz.z - dx.z * dz.x);
}`;

/**
 * Sea bed from the baked shore atlas, and the coast's breakers (scene/waves.ts: `shoaling`,
 * `surfPhase`, `surfZone`, `surfLift` mirror these on the CPU). Needs SURFACE_NOISE and `time`.
 */
const WATER_SHORE = `
// ShoreParams: (shoal depth, surf depth, surf spacing, surf period); (surf height, breaker foam, wash base, wash range).
uniform vec4 shoreShape;
uniform vec4 shoreLook;
vec2 seaBed(vec2 p) {
  for (int i = 0; i < LANDMASS_COUNT; i++) {
    vec4 r = shoreRects[i];
    vec2 uv = (p - r.xy) / (2.0 * r.z) + 0.5;
    if (uv.x > 0.0 && uv.x < 1.0 && uv.y > 0.0 && uv.y < 1.0) {
      uv = clamp(uv, vec2(HALF_TEXEL), vec2(1.0 - HALF_TEXEL));
      vec2 tile = vec2(mod(float(i), SHORE_TILES), floor(float(i) / SHORE_TILES));
      vec4 sampleBed = texture2D(shoreMap, (uv + tile) / SHORE_TILES);
      float height = (sampleBed.r * 65280.0 + sampleBed.g * 255.0) / 65535.0 * SHORE_RANGE + SHORE_MIN;
      return vec2(-height, sampleBed.b);
    }
  }
  // Match the physical atlas floor exactly: never a rectangular shallow halo.
  return vec2(-SHORE_MIN, 0.0);
}
float shoaling(float depth) { return smoothstep(0.0, shoreShape.x, depth); }
// Crests sit on depth contours, so breakers always face the beach, and move shoreward with time.
float surfPhase(vec2 p, float depth) {
  return depth * 6.2831853 / shoreShape.z + time * 6.2831853 / shoreShape.w + vnoise(p * 0.045) * 6.28;
}
float surfZone(float depth) { return (1.0 - smoothstep(shoreShape.y * 0.35, shoreShape.y, depth)) * smoothstep(-0.3, 0.8, depth); }
// Peaked breaker rise above the mean water; E[(0.5 + 0.5 sin)^3] = 5/16 keeps the mean level.
float surfLift(vec2 p, float depth) {
  float s = 0.5 + 0.5 * sin(surfPhase(p, depth));
  return shoreLook.x * surfZone(depth) * (s * s * s - 0.3125);
}`;

/** Ship–sea simulation sampling (scene/water-sim.ts): one texel of (foam, h, ∂h/∂x, ∂h/∂z) over a camera-centred window. */
const WATER_SIM = `
uniform sampler2D simMap;
// (centre x, centre z, extent, 1 while the simulation runs)
uniform vec4 simWindow;
// Fades the field out over the window's outer 3% so its edge never shows; skips the fetch outside it.
vec4 simAt(vec2 p) {
  vec2 uv = (p - simWindow.xy) / simWindow.z + 0.5;
  float fade = simWindow.w * smoothstep(0.0, 0.03, min(min(uv.x, uv.y), min(1.0 - uv.x, 1.0 - uv.y)));
  return fade > 0.0 ? textureLod(simMap, uv, 0.0) * fade : vec4(0.0);
}`;

const WATER_VS = `
precision highp float;
attribute vec3 position;
uniform mat4 world;
uniform mat4 viewProjection;
uniform mat4 reflectionViewProjection;
uniform mat4 refractionViewProjection;
uniform float time;
uniform vec3 cameraPosition;
// Ocean LOD (scene/ocean-mesh.ts): local position.y is the vertex's level; per level
// (spacing m, morph start m, 1 / morph width, 0) over the Chebyshev distance from the camera.
uniform vec4 oceanLod[OCEAN_LEVELS];
// vWave = height / waveBand: raw swell height (sines) or height / max crest (Gerstner).
uniform float waveBand;
${WATER_SIM}
uniform sampler2D shoreMap;
uniform vec4 shoreRects[LANDMASS_COUNT];
varying vec3 vWorld;
varying vec2 vRest;
// (swell kept by shoaling, rest-point water depth).
varying vec2 vShore;
varying float vWave;
varying vec4 vReflection;
varying vec4 vRefraction;
${SURFACE_NOISE}
${WATER_WAVES}
${WATER_SHORE}
void main(void) {
  vec4 wp = world * vec4(position.x, 0.0, position.z, 1.0);
  vec4 lod = oceanLod[int(position.y + 0.5)];
  vec2 away = abs(wp.xz - cameraPosition.xz);
  float morph = clamp((max(away.x, away.y) - lod.y) * lod.z, 0.0, 1.0);
  // Odd vertices slide onto the next level's lattice, where the coarser ring meets this one.
  vec2 cell = floor(wp.xz / lod.x + 0.5);
  wp.xz -= (cell - 2.0 * floor(cell * 0.5)) * lod.x * morph;
  float restDepth = seaBed(wp.xz).x;
  float shoal = shoaling(restDepth);
  vec3 o = waveOffset(wp.xz, lod.x * (1.0 + morph)) * shoal;
  vRest = wp.xz;
  vShore = vec2(shoal, restDepth);
  // Hull-driven waves from the ship–sea simulation ride on top of the swell.
  float simHeight = simAt(wp.xz).y;
  wp.xyz += (o + vec3(0.0, surfLift(wp.xz, restDepth), 0.0)) * waveScale + vec3(0.0, simHeight, 0.0);
  vWave = o.y / waveBand;
  vWorld = wp.xyz;
  vReflection = reflectionViewProjection * wp;
  vRefraction = refractionViewProjection * wp;
  gl_Position = viewProjection * wp;
}`;

const WATER_FS = `
precision highp float;
uniform vec3 cameraPosition;
uniform vec3 fogColor;
uniform float fogDensity;
uniform float time;
// Crest foam source: 0 = height band over vWave, 1 = compression band.
uniform float foamSource;
uniform vec3 sunDir;
uniform vec4 shoreRects[LANDMASS_COUNT];
uniform vec3 rocks[ROCK_COUNT];
uniform sampler2D shoreMap;
uniform highp sampler2D reflectionSampler;
uniform highp sampler2D refractionSampler;
// Sampler precision is separate from float precision: mediump quantizes depth near 1.
uniform highp sampler2D reflectionDepthSampler;
uniform highp sampler2D refractionDepthSampler;
uniform samplerCube environmentSampler;
uniform mat4 reflectionViewProjection;
uniform mat4 inverseReflectionViewProjection;
uniform mat4 inverseRefractionViewProjection;
uniform vec2 captureTexel;
uniform vec2 captureDepthNdc;
uniform float captureDepthAvailable;
// 0 when the graphics preset turns the reflection and refraction captures off.
uniform float captureEnabled;
uniform float captureClearDepth;
// WaveParams: far slope; ripples (scale, strength, speed); crest bands (start, end, amount).
uniform float farSlope;
uniform vec3 rippleA, rippleB, rippleC, crestTint, crestFoam;
${WATER_SIM}
varying vec3 vWorld;
varying vec2 vRest;
varying vec2 vShore;
varying float vWave;
varying vec4 vReflection;
varying vec4 vRefraction;
${SURFACE_NOISE}
${WATER_WAVES}
${WATER_SHORE}
vec2 captureUV(vec4 projected) { return projected.xy / projected.w * 0.5 + 0.5; }
vec2 safeUV(vec2 uv) { return clamp(uv, captureTexel * 1.5, vec2(1.0) - captureTexel * 1.5); }
float waterPath(vec2 uv, float terrainDepth, vec3 view) {
  if (captureDepthAvailable > 0.5) {
    float depth = texture2D(refractionDepthSampler, uv).r;
    if (abs(depth - captureClearDepth) > 0.00001) {
      vec4 bottom = inverseRefractionViewProjection * vec4(uv * 2.0 - 1.0, depth * captureDepthNdc.x + captureDepthNdc.y, 1.0);
      vec3 bottomWorld = bottom.xyz / bottom.w;
      return clamp(length(bottomWorld - vWorld), 0.0, 85.0);
    }
  }
  return max(terrainDepth + vWorld.y, 0.0) / max(abs(view.y), 0.18);
}
void main(void) {
  vec3 view = normalize(cameraPosition - vWorld);
  float dist = length(cameraPosition - vWorld);
  vec2 p = vWorld.xz;
  vec2 bed = seaBed(p);
  float depth = max(bed.x + vWorld.y, 0.0);
  float shallow = 1.0 - smoothstep(0.0, 5.0, depth);
  vec3 detailFade = vec3(1.0 - smoothstep(100.0, 650.0, dist),
    1.0 - smoothstep(40.0, 350.0, dist), 1.0 - smoothstep(15.0, 110.0, dist));
  vec2 slope;
  float compression;
  waveFrame(vRest, dist, slope, compression);
  // Swell flattens over the shallows (the vertex stage shoals displacement the same way).
  slope *= vShore.x;
  compression *= vShore.x;
  slope *= mix(1.0, farSlope, smoothstep(200.0, 900.0, dist));
  // Breaker faces: the lift varies across the shore mostly through depth, so its gradient is
  // d(lift)/d(depth) times the bed's depth gradient (two extra bed samples, not four lifts).
  float surfTheta = surfPhase(p, vShore.y);
  if (vShore.y < shoreShape.y + 1.0) {
    vec2 depthGrad = vec2(seaBed(p + vec2(0.5, 0.0)).x - bed.x, seaBed(p + vec2(0.0, 0.5)).x - bed.x) / 0.5;
    float s = 0.5 + 0.5 * sin(surfTheta);
    slope += depthGrad * shoreLook.x * surfZone(vShore.y) * 1.5 * s * s * cos(surfTheta) * 6.2831853 / shoreShape.z * waveScale;
  }
  // Ship–sea simulation: hull-driven waves tilt normals; churned, foamy water is rougher.
  vec4 sim = simAt(p);
  float cover = sim.x;
  slope += sim.zw;
  if (cover > 0.0) slope += vnoiseGrad(p * 2.3 + vec2(time * 0.6, -time * 0.4)) * 0.16 * cover;
  // Fine wind waves vary reflected ray direction, not just the reflection's brightness.
  slope += mat2(0.8, -0.6, 0.6, 0.8) * vnoiseGrad(mat2(0.8, 0.6, -0.6, 0.8) * p * rippleA.x + vec2(time * 0.17, time * 0.11) * rippleA.z) * rippleA.y * detailFade.x;
  slope += mat2(0.6, 0.8, -0.8, 0.6) * vnoiseGrad(mat2(0.6, -0.8, 0.8, 0.6) * p * rippleB.x + vec2(-time * 0.25, time * 0.19) * rippleB.z) * rippleB.y * detailFade.y;
  slope += vnoiseGrad(p * rippleC.x + vec2(time * 0.38, -time * 0.29) * rippleC.z) * rippleC.y * detailFade.z;
  vec3 n = normalize(vec3(-slope.x, 1.0, -slope.y));
  bool under = cameraPosition.y < vWorld.y;
  if (under) n = -n;
  float ndv = max(dot(n, view), 0.0);
  float fresnel = 0.0204 + 0.9796 * pow(1.0 - ndv, 5.0);
  vec3 reflectedRay = reflect(-view, n);
  // Outside the planar camera, or without captures, coherent prefiltered sky stands in for the scene.
  vec3 reflection = under ? vec3(0.012, 0.075, 0.105) : textureCube(environmentSampler, reflectedRay).rgb;
  float path;
  vec3 transmitted;
  if (captureEnabled > 0.5) {
    vec2 reflectUV = captureUV(vReflection);
    vec2 refractUV = captureUV(vRefraction);
    float edge = smoothstep(0.0, 0.045, min(min(reflectUV.x, reflectUV.y), min(1.0 - reflectUV.x, 1.0 - reflectUV.y)));
    vec2 distortion = slope * (0.012 + 0.016 * (1.0 - ndv)) * edge;
    float reflectionDistance = 2000.0;
    if (captureDepthAvailable > 0.5) {
      float hitDepth = texture2D(reflectionDepthSampler, reflectUV).r;
      if (abs(hitDepth - captureClearDepth) > 0.00001) {
        vec4 hit = inverseReflectionViewProjection * vec4(reflectUV * 2.0 - 1.0, hitDepth * captureDepthNdc.x + captureDepthNdc.y, 1.0);
        reflectionDistance = clamp(length(hit.xyz / hit.w - vWorld), 0.5, 3000.0);
      }
    }
    vec4 reflectedHit = reflectionViewProjection * vec4(vWorld + reflectedRay * reflectionDistance, 1.0);
    vec2 reflectedUV = captureUV(reflectedHit);
    float captureWeight = smoothstep(0.0, 0.04, min(min(reflectedUV.x, reflectedUV.y), min(1.0 - reflectedUV.x, 1.0 - reflectedUV.y)))
      * step(0.001, reflectedHit.w);
    vec2 ruv = safeUV(reflectedUV);
    vec2 tuv = safeUV(refractUV - distortion * mix(0.30, 0.85, shallow));
    // A narrow five-tap footprint preserves actual masts/rocks/clouds but avoids
    // razor-sharp mirrored geometry on a visibly rough surface.
    vec2 footprint = captureTexel * mix(1.25, 3.0, 1.0 - detailFade.y);
    vec3 captured = texture2D(reflectionSampler, ruv).rgb * 0.40;
    captured += (texture2D(reflectionSampler, safeUV(ruv + vec2(footprint.x, 0.0))).rgb
      + texture2D(reflectionSampler, safeUV(ruv - vec2(footprint.x, 0.0))).rgb
      + texture2D(reflectionSampler, safeUV(ruv + vec2(0.0, footprint.y))).rgb
      + texture2D(reflectionSampler, safeUV(ruv - vec2(0.0, footprint.y))).rgb) * 0.15;
    reflection = mix(reflection, captured, captureWeight);
    path = waterPath(tuv, depth, view);
    transmitted = texture2D(refractionSampler, tuv).rgb;
  } else {
    // Captures off (Low preset): the sea bed's depth sets the light's path through the water,
    // lit sand stands in for the bed, and from below the sky shows through the surface.
    path = depth / max(abs(view.y), 0.18);
    transmitted = under ? textureCube(environmentSampler, -view).rgb : vec3(0.62, 0.55, 0.38);
  }
  vec3 absorption = exp(-vec3(0.24, 0.066, 0.035) * min(path, 85.0));
  vec3 scattering = vec3(0.007, 0.087, 0.150);
  if (under) {
    absorption = exp(-vec3(0.16, 0.055, 0.038) * min(dist, 35.0));
    scattering = vec3(0.018, 0.17, 0.21);
    // Total internal reflection outside Snell's window.
    fresnel = mix(fresnel, 1.0, 1.0 - smoothstep(0.64, 0.72, ndv));
  }
  vec3 col = mix(scattering + (transmitted - scattering) * absorption, reflection, fresnel);
  float sunFacing = max(dot(n, sunDir), 0.0);
  vec3 halfVector = normalize(sunDir + view);
  float ndh = max(dot(n, halfVector), 0.0);
  float glint = pow(ndh, mix(140.0, 480.0, detailFade.y)) * mix(0.8, 2.6, detailFade.y);
  col += vec3(1.0, 0.91, 0.72) * (glint + pow(ndh, 42.0) * 0.10) * sunFacing * (under ? 0.0 : 1.0);
  col += vec3(0.013, 0.060, 0.052) * smoothstep(crestTint.x, crestTint.y, vWave) * crestTint.z * (1.0 - shallow) * sunFacing;

  // Shore wash: a broken swash line that swings with each arriving breaker, not an island-wide outline.
  float breakup = vnoise(p * 0.82 + vec2(time * 0.19, -time * 0.13));
  float fine = vnoise(p * 3.7 - vec2(time * 0.24, time * 0.18));
  float washFront = shoreLook.z + shoreLook.w * (0.5 + 0.5 * sin(surfPhase(p, 0.0)));
  float shoreBand = (1.0 - smoothstep(0.04, 0.24, abs(depth - washFront)))
    * (1.0 - smoothstep(0.7, 1.25, depth));
  float foam = shoreBand * smoothstep(0.51, 0.77, breakup) * smoothstep(0.22, 0.67, fine) * mix(0.45, 1.0, bed.y);
  // Breakers: foam caps the crests as they roll in, and a broken sheet trails them in the inner surf.
  float zone = surfZone(vShore.y), surf = 0.5 + 0.5 * sin(surfTheta);
  float inner = 1.0 - smoothstep(shoreShape.y * 0.25, shoreShape.y * 0.6, vShore.y);
  float breakers = zone * (smoothstep(0.8, 0.97, surf) * smoothstep(0.3, 0.7, breakup * 0.5 + fine * 0.5)
    + inner * smoothstep(0.45, 0.8, surf) * smoothstep(0.5, 0.8, fine) * 0.45);
  foam = max(foam, breakers * shoreLook.y);
  for (int i = 0; i < ROCK_COUNT; i++) {
    float rockDistance = length(p - rocks[i].xy) - rocks[i].z;
    float rockWash = (1.0 - smoothstep(0.12, 1.0, abs(rockDistance - washFront)))
      * smoothstep(0.55, 0.80, breakup) * smoothstep(0.3, 0.75, fine);
    foam = max(foam, rockWash * 0.5);
  }
  // Height-band crests are sparse sparkles; compression whitecaps keep a streaky, broken body.
  float crests = foamSource > 0.5
    ? smoothstep(crestFoam.x, crestFoam.y, compression) * smoothstep(0.25, 0.75, breakup * 0.6 + fine * 0.4) * crestFoam.z * detailFade.x
    : smoothstep(crestFoam.x, crestFoam.y, vWave) * smoothstep(0.68, 0.90, breakup) * crestFoam.z * detailFade.x;
  // Foam from the simulation (breaking hull waves, hull churn) breaks up into patches and streaks as it thins.
  float simFoam = 0.0;
  if (cover > 0.0) {
    float churn = vnoise(p * 1.3 + vec2(time * 0.3, -time * 0.2)) * 0.5 + vnoise(p * 4.1 - vec2(time * 0.5, time * 0.35)) * 0.5;
    simFoam = smoothstep(0.08, 0.7, cover * (0.3 + 1.2 * churn));
  }
  foam = max(max(foam, crests), simFoam) * (under ? 0.0 : 1.0);
  col = mix(col, vec3(0.65, 0.76, 0.75), foam * 0.72);
  float fog = clamp(1.0 - exp(-pow(dist * fogDensity, 2.0)), 0.0, 1.0);
  gl_FragColor = vec4(mix(col, fogColor, fog), 1.0);
}`;

/** Real capture samplers are supplied by OceanCaptures; water writes opaque depth. */
export function waterShaders(landmassCount: number, rockCount: number, shoreTiles: number): { vertex: string; fragment: string } {
  const defines = [
    `#define LANDMASS_COUNT ${landmassCount}`,
    `#define ROCK_COUNT ${rockCount}`,
    `#define SHORE_TILES ${shoreTiles.toFixed(1)}`,
    `#define SHORE_MIN ${SHORE.min.toFixed(1)}`,
    `#define SHORE_RANGE ${SHORE.range.toFixed(1)}`,
    `#define HALF_TEXEL (0.5 / ${SHORE.tile.toFixed(1)})`,
    `#define OCEAN_LEVELS ${OCEAN_LEVELS}`,
    `#define OCEAN_SPACING ${OCEAN_SPACING.toFixed(1)}`,
  ].join("\n");
  return { vertex: `${defines}\n${WATER_VS}`, fragment: `${defines}\n${WATER_FS}` };
}
